import { useAuthState } from "react-redux-django-auth";
import { 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  User, 
  CheckCircle2, 
  ShieldCheck,
  Briefcase,
  GitBranch,
  ExternalLink
} from "lucide-react";

const ProfileOverview = () => {
  const { user: rawUser } = useAuthState();
  const currentUser = (rawUser as any)?.user || (rawUser as any)?.data?.user || rawUser;

  const formatRole = (r?: string) => {
    if (!r) return "";
    const lower = r.toLowerCase();
    if (lower === "superadmin") return "Super Administrator";
    if (lower === "admin") return "Administrator";
    if (lower === "instructor") return "Instructor";
    if (lower === "student") return "Student";
    return r.charAt(0).toUpperCase() + r.slice(1).toLowerCase();
  };

  const resolvedRole =
    formatRole(currentUser?.role) ||
    formatRole(currentUser?.user_type) ||
    (currentUser?.is_superuser
      ? "Super Administrator"
      : currentUser?.is_staff
        ? "Administrator"
        : "Student");

  const infoGroups = [
    {
      title: "Contact Information",
      items: [
        { icon: Mail, label: "Email Address", value: currentUser?.email, verified: true },
        { icon: Phone, label: "Phone Number", value: currentUser?.phone_number || "Not provided" },
        { icon: MapPin, label: "Address", value: currentUser?.home_address || "Not provided" },
      ]
    },
    {
      title: "Developer Accounts",
      items: [
        {
          icon: GitBranch,
          label: "GitHub Account",
          value: currentUser?.github_username ? `@${currentUser.github_username}` : "Not connected",
          verified: Boolean(currentUser?.github_username),
          url: currentUser?.github_username ? `https://github.com/${currentUser.github_username}` : undefined,
        },
      ]
    },
    {
      title: "Basic Information",
      items: [
        { icon: User, label: "Gender", value: currentUser?.gender || "Not specified" },
        { icon: Calendar, label: "Date of Birth", value: currentUser?.date_of_birth || "Not provided" },
        { icon: ShieldCheck, label: "Account Type", value: resolvedRole },
      ]
    },
    {
      title: "Location Details",
      items: [
        { icon: Briefcase, label: "Organization", value: "Codehub Tech Studio" },
        { icon: MapPin, label: "Primary Address", value: currentUser?.home_address || "Not provided" },
      ]
    }
  ];

  return (
    <div className="cht-space-y-12">
      <header>
        <h2 className="cht-text-2xl cht-font-black cht-text-black">Profile Information</h2>
        <p className="cht-text-sm cht-text-gray-400 cht-mt-2">This is how other members of the community see you.</p>
      </header>

      <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 cht-gap-10">
        {infoGroups.map((group, idx) => (
          <div key={idx} className="cht-space-y-6">
            <h4 className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-[0.2em]">{group.title}</h4>
            <div className="cht-space-y-6">
              {group.items.map((item: any, i: number) => (
                <div key={i} className="cht-flex cht-items-start cht-gap-4">
                  <div className="cht-w-10 cht-h-10 cht-rounded-xl cht-bg-gray-50 cht-flex cht-items-center cht-justify-center cht-text-gray-400 cht-shrink-0">
                    <item.icon size={18} />
                  </div>
                  <div className="cht-space-y-1">
                    <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">{item.label}</p>
                    <div className="cht-flex cht-items-center cht-gap-2">
                      {item.url ? (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cht-text-sm cht-font-bold cht-text-[#3e57da] hover:cht-underline cht-inline-flex cht-items-center cht-gap-1"
                        >
                          {item.value}
                          <ExternalLink size={12} />
                        </a>
                      ) : (
                        <p className="cht-text-sm cht-font-bold cht-text-black">{item.value}</p>
                      )}
                      {item.verified && <CheckCircle2 size={14} className="cht-text-green-500" />}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Completion Progress or something similar */}
      <div className="cht-bg-gray-50/50 cht-p-8 cht-rounded-[32px] cht-border cht-border-gray-100 cht-flex cht-flex-col md:cht-flex-row cht-items-center cht-justify-between cht-gap-6">
        <div className="cht-space-y-2">
          <h4 className="cht-text-sm cht-font-black cht-text-black">Profile Completion</h4>
          <p className="cht-text-xs cht-font-medium cht-text-gray-400">Complete your profile to unlock more features.</p>
        </div>
        <div className="cht-flex cht-items-center cht-gap-4 cht-w-full md:cht-w-64">
          <div className="cht-flex-1 cht-h-2 cht-bg-gray-200 cht-rounded-full cht-overflow-hidden">
            <div className="cht-w-4/5 cht-h-full cht-bg-[#3e57da] cht-rounded-full"></div>
          </div>
          <span className="cht-text-xs cht-font-black cht-text-black">80%</span>
        </div>
      </div>
    </div>
  );
};

export default ProfileOverview;
